import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import LanguageSwitcher from './LanguageSwitcher';
import ThemeSelector from './ThemeSelector';
import SmsCabinetBackButton from './SmsCabinetBackButton';
import SmsHeaderBalance from './SmsHeaderBalance';
import SmsTopUpBalanceButton from './SmsTopUpBalanceButton';
import { SMS_PAGE, smsPageTokens, useSmsPageDesign } from '../../theme/smsPageDesign';
import { useTranslation } from 'react-i18next';

/** Двухъярусная шапка: тёмный блок бренда + полоса техподдержки цветом SMS_PAGE.accent (как кнопки и акценты в приложении). */
const SmsSiteHeader: React.FC = () => {
  const { t } = useTranslation();
  const { isSmsLight } = useSmsPageDesign();
  const tokens = smsPageTokens(isSmsLight);
  const darkTop = '#252839';
  const lowerBg = SMS_PAGE.accent;
  const lowerText = '#252839';
  const lowerDivider = 'rgba(37, 40, 57, 0.35)';
  const topUpBg = SMS_PAGE.white;
  const topUpHoverBg = '#fafafa';
  const headerPadX = { xs: 2, sm: 3 } as const;

  const logoRowRef = useRef<HTMLDivElement | null>(null);
  const taglineRef = useRef<HTMLDivElement | null>(null);
  const [logoInnerPx, setLogoInnerPx] = useState(0);
  const [taglineFontRem, setTaglineFontRem] = useState(0.46);
  const siteTagline = t('layout.siteTagline');

  useLayoutEffect(() => {
    const logo = logoRowRef.current;
    const tag = taglineRef.current;
    if (!logo || !tag) return;

    const fit = () => {
      const maxW = logo.getBoundingClientRect().width;
      if (maxW < 8) return;
      setLogoInnerPx(maxW);

      let lo = 0.24;
      let hi = 0.56;
      for (let i = 0; i < 20; i++) {
        const mid = (lo + hi) / 2;
        tag.style.fontSize = `${mid}rem`;
        const fits = tag.scrollWidth <= maxW + 0.5;
        if (fits) lo = mid;
        else hi = mid;
      }
      tag.style.fontSize = '';
      const next = Math.round(lo * 10000) / 10000;
      setTaglineFontRem((prev) => (Math.abs(prev - next) < 0.0005 ? prev : next));
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(logo);
    return () => {
      ro.disconnect();
    };
  }, [siteTagline]);

  return (
    <Box
      component="header"
      sx={{
        width: '100%',
        flexShrink: 0,
        '& a': { textDecoration: 'none' },
      }}
    >
      <Box
        sx={{
          bgcolor: darkTop,
          minHeight: 64,
          px: headerPadX,
          py: { xs: 1.25, sm: 1.5 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <Box sx={{ flexShrink: 0, lineHeight: 0, display: 'flex', alignItems: 'center' }}>
            <SmsCabinetBackButton iconOnlyDark />
          </Box>
          <Box
            sx={{
              display: 'inline-flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              maxWidth: '100%',
              minWidth: 0,
            }}
          >
            <Box ref={logoRowRef} sx={{ flexShrink: 0, lineHeight: 0 }}>
              <Typography
                component="span"
                sx={{
                  fontFamily: '"Montserrat", sans-serif',
                  fontWeight: 700,
                  fontSize: { xs: '1.125rem', sm: '1.25rem' },
                  lineHeight: 1.15,
                  letterSpacing: '0.02em',
                  whiteSpace: 'nowrap',
                }}
              >
                <Box component="span" sx={{ color: '#fff' }}>
                  SMS
                </Box>
                <Box component="span" sx={{ color: SMS_PAGE.accent }}>
                  .BY
                </Box>
              </Typography>
            </Box>
            <Typography
              ref={taglineRef}
              variant="caption"
              sx={{
                display: 'block',
                color: 'rgba(255,255,255,0.55)',
                fontFamily: '"Montserrat", sans-serif',
                fontSize: `${taglineFontRem}rem`,
                lineHeight: 1.2,
                fontWeight: 500,
                letterSpacing: '-0.045em',
                boxSizing: 'border-box',
                whiteSpace: 'nowrap',
                mt: '2px',
                maxWidth: logoInnerPx > 0 ? `${logoInnerPx}px` : undefined,
                overflow: 'hidden',
                textOverflow: 'clip',
              }}
            >
              {siteTagline}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <LanguageSwitcher inverted />
          <ThemeSelector inverted />
        </Box>
      </Box>

      <Box
        sx={{
          bgcolor: lowerBg,
          borderBottom: `1px solid ${isSmsLight ? 'rgba(37,40,57,0.08)' : tokens.outlineBorder}`,
          minHeight: 64,
          px: headerPadX,
          py: { xs: 1.25, sm: 1.5 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 2, md: 3 },
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
            gap: '4px',
            minWidth: 0,
            flex: { md: '0 1 auto' },
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 500,
              fontSize: 13,
              lineHeight: '18px',
              color: lowerText,
            }}
          >
            {t('layout.headerTechLabel')}
          </Typography>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              columnGap: 1,
              rowGap: 0.25,
            }}
          >
            <Typography
              component="a"
              href={`tel:${t('layout.headerTechTelHref')}`}
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontWeight: 600,
                fontSize: 15,
                lineHeight: '22px',
                color: lowerText,
                whiteSpace: 'nowrap',
              }}
            >
              {t('layout.headerTechPhone')}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontWeight: 400,
                fontSize: 15,
                lineHeight: '22px',
                color: lowerDivider,
                userSelect: 'none',
              }}
            >
              |
            </Typography>
            <Typography
              component="a"
              href={`mailto:${t('layout.headerTechEmail')}`}
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontWeight: 600,
                fontSize: 15,
                lineHeight: '22px',
                color: lowerText,
                whiteSpace: 'nowrap',
              }}
            >
              {t('layout.headerTechEmail')}
            </Typography>
          </Box>
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          flexWrap="wrap"
          sx={{
            flexShrink: 0,
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
            alignItems: 'center',
          }}
        >
          <SmsHeaderBalance
            variant="stacked"
            sx={{ color: lowerText, alignItems: 'flex-start', textAlign: 'left' }}
          />
          <SmsTopUpBalanceButton
            size="medium"
            sx={{
              bgcolor: topUpBg,
              color: lowerText,
              borderRadius: '24px',
              px: 3,
              py: 0.75,
              minHeight: 36,
              border: 'none',
              boxShadow: isSmsLight ? '2px 4px 4px rgba(37,40,57,0.15)' : tokens.shadow,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 16,
              lineHeight: '24px',
              '&:hover': {
                bgcolor: topUpHoverBg,
                border: 'none',
                boxShadow: isSmsLight ? '2px 4px 8px rgba(37,40,57,0.2)' : '0px 2px 10px rgba(0,0,0,0.65)',
              },
            }}
          />
        </Stack>
      </Box>
    </Box>
  );
};

export default SmsSiteHeader;
